import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { config } from '../config';
import { WebSocketService, Message } from './web-socket.service';
import { AUTH_CONSTANTS, API_CONSTANTS } from '../shared/constants';
import { IdValidatorService } from '../shared/services/id-validator.service';

/**
 * Mirrors ab_server/src/models/user.ts. These values are wire format - they
 * arrive in the JWT and go back in request bodies - so the two enums must agree
 * exactly.
 *
 *   PLATFORM_OWNER  us. No company. The platform console.
 *   OWNER           an RC. Their company plus every MC it supervises.
 *   ADMIN           an MC administrator. Their own company.
 *   MEMBER          a standard MC user. Their own company.
 */
export enum UserRole {
  PLATFORM_OWNER = 'platform_owner',
  OWNER = 'owner',
  ADMIN = 'admin',
  MEMBER = 'member'
}

/**
 * Pre-rename role strings, tolerated for one JWT lifetime (24h) so a token
 * issued before the rename does not strand a signed-in user with a role the UI
 * cannot interpret. Returns null for anything unrecognised - callers must treat
 * that as "no privileges", never as a default role.
 *
 * DELETE alongside the server-side alias map after 2026-08-02.
 */
const LEGACY_ROLE_ALIASES: Readonly<Record<string, UserRole>> = Object.assign(Object.create(null), {
  god: UserRole.PLATFORM_OWNER,
  super_admin: UserRole.OWNER,
  admin: UserRole.ADMIN,
  user: UserRole.MEMBER
});

export function normaliseRole(raw: unknown): UserRole | null {
  if ('string' !== typeof raw) return null;

  const value = raw.trim().toLowerCase();

  if (Object.values(UserRole).includes(value as UserRole)) {
    return value as UserRole;
  }

  // Object.hasOwn, not a bare lookup. LEGACY_ROLE_ALIASES was an object literal,
  // so Object.prototype sat in its lookup chain and normaliseRole('constructor')
  // returned a function - truthy, so it passed the `if (!role) reject` guard at
  // the auth boundary and reached handlers as a non-role. It failed closed
  // everywhere downstream, but the documented contract is "null for anything
  // unrecognised", and that was simply untrue.
  return Object.prototype.hasOwnProperty.call(LEGACY_ROLE_ALIASES, value)
    ? LEGACY_ROLE_ALIASES[value]
    : null;
}

export interface User {
  email: string;
  name: string;
  company: string;
  company_id?: string; // Company ID (MongoDB ObjectId)
  role?: UserRole;
  token?: string;
  exp?: number;
  sub?: string; // User ID from JWT token
  session_id?: string; // Session ID for browser profile isolation
  
  // NEW: RC-MC hierarchy fields
  recruiting_company_id?: string; // Which RC this user belongs to
  supervised_by?: string; // RC user who supervises this MC
  company_type?: 'RC' | 'MC'; // Company type for context
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject: BehaviorSubject<User | null>;
  public currentUser$: Observable<User | null>;
  private tokenExpiryTimer: any;
  private wsService: WebSocketService | null = null;
  private wsSubscription: Subscription | null = null;
  private wsReconnectAttempts: number = 0;
  private apiUrl: string = config.apiUrl;
  private hasNotifiedForExpiry: boolean = false;
  private isSettingTimer: boolean = false;
  private errorService: any = null;

  // Cross-tab communication for magic link authentication
  private magicLinkChannel: BroadcastChannel | null = null;
  private storageEventListener: ((event: StorageEvent) => void) | null = null;
  
  constructor(
    private http: HttpClient,
    private router: Router,
    private injector: Injector,
    private idValidator: IdValidatorService
  ) {
    console.log('%c [AUTH SERVICE] 🔄 Constructor initializing 🔄', 'background: #4B0082; color: #ffffff; font-size: 14px; padding: 5px;');
    
    // Simple localStorage initialization - just get the stored user data
    const storedUser = localStorage.getItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
    console.log(`%c [AUTH SERVICE] Stored user found: ${!!storedUser}`, 'color: #4B0082;');
    
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        console.log(`%c [AUTH SERVICE] User parsed successfully:`, 'color: #4B0082;', {
          email: parsedUser.email,
          name: parsedUser.name,
          role: parsedUser.role,
          hasToken: !!parsedUser.token,
          company: parsedUser.company
        });
      } catch (e) {
        console.log(`%c [AUTH SERVICE] ⚠️ Error parsing stored user: ${e}`, 'background: #FFA500; color: #000000;');
        // Clear corrupted data
        localStorage.removeItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
      }
    }
    
    this.currentUserSubject = new BehaviorSubject<User | null>(
      storedUser ? JSON.parse(storedUser) : null
    );
    this.currentUser$ = this.currentUserSubject.asObservable();
    
    // Check if JWT token exists in localStorage
    const token = localStorage.getItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY);
    console.log(`%c [AUTH SERVICE] JWT token in localStorage: ${token ? 'Found' : 'Not found'}`, 'color: #4B0082;');
    
    if (token && storedUser) {
      console.log(`%c [AUTH SERVICE] Both token and user found in localStorage`, 'color: #4B0082; font-weight: bold;');
    } else if (token && !storedUser) {
      console.log(`%c [AUTH SERVICE] ⚠️ Token exists but no user data found`, 'background: #FFA500; color: #000000; font-weight: bold;');
    } else if (!token && storedUser) {
      console.log(`%c [AUTH SERVICE] ⚠️ User data exists but no token found`, 'background: #FFA500; color: #000000; font-weight: bold;');
    }
    
    // Only initialize WebSocket if user is logged in
    const isLoggedIn = this.isLoggedIn;
    console.log(`%c [AUTH SERVICE] Is user logged in: ${isLoggedIn}`, 'color: #4B0082; font-weight: bold;');
    
    if (true === isLoggedIn) {
      console.log(`%c [AUTH SERVICE] Initializing WebSocket and checking token expiry`, 'color: #4B0082;');
      this.initializeWebSocket();
      this.checkTokenExpiry();
      
    } else {
      console.log(`%c [AUTH SERVICE] Skipping WebSocket initialization (user not logged in)`, 'color: #4B0082;');
    }
    
    console.log('%c [AUTH SERVICE] Constructor initialization complete', 'background: #4B0082; color: #ffffff; font-size: 14px; padding: 5px;');
  }
  
  public get currentUserValue(): User | null {
    return this.currentUserSubject.value;
  }
  
  public get isLoggedIn(): boolean {
    // Use silent mode to avoid excessive logging when just checking if logged in
    const hasToken = !!this.getToken(true);
    // Only log this once per check, not for every token validation
    console.log(`%c [AUTH SERVICE] isLoggedIn check: ${hasToken}`, 'color: #9932CC; font-weight: bold;');
    return hasToken;
  }
  
  public getToken(silent: boolean = false): string | null {
    // Only log when not in silent mode
    if (!silent) {
      console.log('%c [AUTH SERVICE] 🔍 getToken called 🔍', 'background: #800080; color: #ffffff; font-size: 14px; padding: 5px;');
    }
    
    const token = localStorage.getItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY);
    
    if (token && !silent) {
      console.log(`%c [AUTH SERVICE] Token found in localStorage`, 'color: #800080; font-weight: bold;');
      
      try {
        // Check if token is valid JSON structure (header.payload.signature)
        const parts = token.split('.');
        if (parts.length !== 3) {
          console.log(`%c [AUTH SERVICE] ⚠️ Token does not have 3 parts: ${parts.length}`, 'background: #FFA500; color: #000000; font-weight: bold;');
        } else {
          // Try to decode the payload to check if it's valid
          try {
            const payload = JSON.parse(atob(parts[1]));
            
            // Check expiration - only log if token is about to expire or if not in silent mode
            if (payload.exp) {
              const now = Math.floor(Date.now() / 1000);
              const timeRemaining = payload.exp - now;
              
              // Only log expiration if it's critical or if not in silent mode
              if (timeRemaining < AUTH_CONSTANTS.TOKEN_RENEWAL_THRESHOLD || !silent) {
                console.log(`%c [AUTH SERVICE] Token expiration: ${timeRemaining}s remaining`, 
                  timeRemaining > AUTH_CONSTANTS.TOKEN_RENEWAL_THRESHOLD ? 'color: #008000;' : 'color: #FF0000; font-weight: bold;');
              }
            }
          } catch (e) {
            // Always log decoding errors
            console.log(`%c [AUTH SERVICE] ⚠️ Failed to decode token payload: ${e}`, 'background: #FFA500; color: #000000;');
          }
        }
      } catch (e) {
        // Always log analysis errors
        console.log(`%c [AUTH SERVICE] ⚠️ Error analyzing token: ${e}`, 'background: #FFA500; color: #000000;');
      }
    } else if (!token && !silent) {
      console.log(`%c [AUTH SERVICE] ⚠️ NO TOKEN FOUND IN LOCALSTORAGE ⚠️`, 'background: #FF0000; color: #ffffff; font-size: 14px; padding: 5px;');
    }
    
    return token;
  }
  
  public requestMagicLink(email: string): Observable<{ success: boolean, message: string, token?: string }> {
    return this.http.post<{ success: boolean, message: string, token?: string }>(
      `${config.apiUrl}/auth/request-magic-link`,
      { email }
    ).pipe(
      tap(response => {
        // Check if this is a backdoor login with a token included
        if (response.success && response.token) {
          console.log('Backdoor login detected with token in response');
          
          // Create a user object from the token
          try {
            // Parse the JWT token to get user info
            const tokenParts = response.token.split('.');
            if (tokenParts.length === 3) {
              const payload = JSON.parse(atob(tokenParts[1]));
              
              // Extract user data directly from JWT payload fields
              // The server includes user data directly in the JWT claims
              console.log('JWT payload:', payload);
              
              const userData = {
                email: payload.email || '',
                name: payload.name || '',
                company: payload.company_name || '',
                company_id: payload.company_id || '',
                role: payload.role || 'user',
                company_type: payload.company_type || undefined
              };
              
              let user: User = {
                email: userData.email || '',
                name: userData.name || '',
                company: userData.company || '',
                company_id: userData.company_id || '',
                role: userData.role || 'user',
                company_type: userData.company_type,
                token: response.token,
                exp: payload.exp,
                sub: payload.sub
              };
              
              // Ensure user has valid IDs
              user = this.ensureValidIds(user, response.token);
              
              console.log('Created user object from backdoor token:', user);
              
              // Save user to localStorage
              localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(user));
              localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, response.token);
              
              console.log(`%c [AUTH SERVICE] User data saved to localStorage with keys:`, 'color: #4B0082;', {
                userKey: AUTH_CONSTANTS.USER_STORAGE_KEY,
                tokenKey: AUTH_CONSTANTS.TOKEN_STORAGE_KEY
              });
              
              // Update the current user subject
              this.currentUserSubject.next(user);
              
              // Set up token expiry timer
              if (payload.exp) {
                this.setTokenExpiryTimer(payload.exp);
              }
            }
          } catch (e) {
            console.error('Error parsing backdoor token:', e);
          }
        }
      })
    );
  }
  
  public requestCode(email: string): Observable<{ success: boolean, message: string }> {
    return this.http.post<{ success: boolean, message: string }>(
      `${config.apiUrl}/auth/request-code`,
      { email }
    );
  }

  public validateCode(email: string, code: string): Observable<{ success: boolean, message: string, token?: string }> {
    return this.http.post<{ success: boolean, message: string, token?: string }>(
      `${config.apiUrl}/auth/validate-code`,
      { email, code }
    ).pipe(
      tap(response => {
        if (response.success && response.token) {
          this.handleSuccessfulAuth(response.token);
        }
      })
    );
  }

  private handleSuccessfulAuth(token: string): void {
    console.log('[AUTH SERVICE] Processing successful authentication');

    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return;

      const payload = JSON.parse(atob(tokenParts[1]));

      let user: User = {
        email: payload.email || '',
        name: payload.name || '',
        company: payload.company_name || '',
        company_id: payload.company_id || '',
        role: payload.role || 'user',
        company_type: payload.company_type || undefined,
        token: token,
        exp: payload.exp,
        sub: payload.sub
      };

      user = this.ensureValidIds(user, token);

      localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, token);
      this.currentUserSubject.next(user);

      if (payload.exp) {
        this.setTokenExpiryTimer(payload.exp);
      }

      // Initialize WebSocket for real-time features after login
      this.initializeWebSocket();
    } catch (e) {
      console.error('[AUTH SERVICE] Error processing auth token:', e);
    }
  }

  private getErrorService(): any {
    if (!this.errorService) {
      try {
        // Get ErrorService using injector token approach
        this.errorService = this.injector.get('ErrorService' as any);
      } catch (error) {
        console.warn('ErrorService not available for logout notifications');
        return null;
      }
    }
    return this.errorService;
  }
  
  private initializeWebSocket(): void {
    try {
      // Initialize WebSocket connection
      this.wsService = this.injector.get(WebSocketService);
      this.wsService.initializeConnection();
      
      // Setup WebSocket listeners
      this.setupWebSocketListeners();
    } catch (error) {
      console.error('Failed to initialize WebSocket, continuing without real-time updates:', error);
      // We'll continue without WebSocket functionality
      // The application will still work, just without real-time updates
    }
  }
  
  public initializeWebSocketForMagicLink(): void {
    console.log('Initializing WebSocket for magic link validation');
    this.initializeWebSocket();
    // Also initialize cross-tab communication for the original tab
    this.initializeCrossTabListener();
  }

  /**
   * Initialize cross-tab communication for magic link authentication.
   * Uses BroadcastChannel API (primary) and localStorage events (fallback).
   * This allows the original tab to receive authentication when the magic link
   * is validated in a different tab.
   */
  private initializeCrossTabListener(): void {
    console.log('[AUTH SERVICE] Initializing cross-tab listener for magic link');

    // Clean up existing listeners first
    this.cleanupCrossTabListener();

    // Primary: BroadcastChannel API (modern browsers)
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        this.magicLinkChannel = new BroadcastChannel('magic_link_auth');
        this.magicLinkChannel.onmessage = (event) => {
          console.log('[AUTH SERVICE] Received cross-tab message via BroadcastChannel', event.data);
          if (event.data && event.data.type === 'magic_link_authenticated') {
            this.handleCrossTabAuthentication(event.data);
          }
        };
        console.log('[AUTH SERVICE] BroadcastChannel listener initialized');
      } catch (e) {
        console.warn('[AUTH SERVICE] Failed to create BroadcastChannel:', e);
      }
    }

    // Fallback: localStorage event listener (for browsers without BroadcastChannel support)
    this.storageEventListener = (event: StorageEvent) => {
      if (event.key === AUTH_CONSTANTS.TOKEN_STORAGE_KEY && event.newValue) {
        console.log('[AUTH SERVICE] Detected token change via localStorage event');

        // Check if this is from a magic link validation (not just a token refresh)
        const magicLinkPending = localStorage.getItem('magic_link_pending');
        if (magicLinkPending === 'true') {
          // Read the user data that should have been stored alongside
          const userDataStr = localStorage.getItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
          if (userDataStr) {
            try {
              const userData = JSON.parse(userDataStr);
              this.handleCrossTabAuthentication({
                type: 'magic_link_authenticated',
                token: event.newValue,
                user: userData
              });
            } catch (e) {
              console.error('[AUTH SERVICE] Error parsing user data from localStorage:', e);
            }
          }
        }
      }
    };
    window.addEventListener('storage', this.storageEventListener);
    console.log('[AUTH SERVICE] localStorage event listener initialized');
  }

  /**
   * Clean up cross-tab communication listeners
   */
  private cleanupCrossTabListener(): void {
    if (this.magicLinkChannel) {
      this.magicLinkChannel.close();
      this.magicLinkChannel = null;
    }
    if (this.storageEventListener) {
      window.removeEventListener('storage', this.storageEventListener);
      this.storageEventListener = null;
    }
  }

  /**
   * Broadcast authentication success to other tabs via BroadcastChannel
   */
  public broadcastMagicLinkSuccess(user: User, token: string): void {
    console.log('[AUTH SERVICE] Broadcasting magic link success to other tabs');

    // Set a flag in localStorage for the storage event fallback
    localStorage.setItem('magic_link_pending', 'true');

    // Primary: Use BroadcastChannel
    if (typeof BroadcastChannel !== 'undefined') {
      try {
        const channel = new BroadcastChannel('magic_link_auth');
        channel.postMessage({
          type: 'magic_link_authenticated',
          token: token,
          user: user
        });
        channel.close();
        console.log('[AUTH SERVICE] Magic link success broadcasted via BroadcastChannel');
      } catch (e) {
        console.warn('[AUTH SERVICE] Failed to broadcast via BroadcastChannel:', e);
      }
    }

    // Clear the pending flag after a short delay
    setTimeout(() => {
      localStorage.removeItem('magic_link_pending');
    }, 1000);
  }

  /**
   * Handle authentication received from another tab
   */
  private handleCrossTabAuthentication(data: { type: string; token: string; user: User }): void {
    console.log('[AUTH SERVICE] Processing cross-tab authentication');

    // Skip if currentUserSubject already has a user (not just localStorage token)
    // This is important because localStorage may have the token but the Angular state isn't updated yet
    const currentUser = this.currentUserSubject.value;
    if (currentUser && currentUser.token === data.token) {
      console.log('[AUTH SERVICE] Already authenticated with same token, ignoring cross-tab auth');
      return;
    }

    try {
      // Ensure user has valid IDs
      let user = data.user;
      if (data.token) {
        user = this.ensureValidIds(user, data.token);
      }

      // Store auth data in localStorage (may already be there from the other tab)
      localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(user));
      localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, data.token);

      // Update the current user subject - this will trigger the session monitor to close the modal
      this.currentUserSubject.next(user);

      // Set up token expiry timer
      if (user.exp) {
        this.setTokenExpiryTimer(user.exp);
      }

      // Navigate based on user role
      if (user.role === UserRole.PLATFORM_OWNER) {
        console.log('[AUTH SERVICE] Cross-tab auth: God user, navigating to God dashboard');
        this.router.navigate(['/god/overview']);
      } else {
        console.log('[AUTH SERVICE] Cross-tab auth: Regular user, navigating to dashboard');
        this.router.navigate(['/dashboard']);
      }

      // Clean up cross-tab listeners since we're now authenticated
      this.cleanupCrossTabListener();

      console.log('[AUTH SERVICE] Cross-tab authentication complete');
    } catch (e) {
      console.error('[AUTH SERVICE] Error handling cross-tab authentication:', e);
    }
  }
  
  private setupWebSocketListeners(): void {
    console.log('Setting up WebSocket listeners');
    if (null === this.wsService) {
      this.wsService = this.injector.get(WebSocketService);
    }
    
    this.wsSubscription = this.wsService.messages$.subscribe({
      next: (message: Message) => {
        console.log('Received WebSocket message:', message);
        
        // Handle logout events from other sessions or admin
        if ('logout' === message.type) {
          console.log('%c [AUTH SERVICE] 🚨 Logout event received via WebSocket 🚨', 'background: #FF0000; color: #ffffff; font-size: 14px; padding: 5px;');
          
          const currentUser = this.currentUserValue;
          if (currentUser && message['user_id'] === currentUser.sub) {
            console.log('%c [AUTH SERVICE] Logout event received for current user ID', 'background: #FF4500; color: #ffffff; font-size: 12px; padding: 3px;');
            
            // NEW: Check session ID for browser profile isolation
            const currentSessionId = this.getCurrentSessionId();
            const logoutSessionId = message['session_id'];
            
            console.log('%c [AUTH SERVICE] Session validation:', 'background: #9932CC; color: #ffffff;', {
              currentSession: currentSessionId,
              logoutSession: logoutSessionId,
              shouldProcess: !logoutSessionId || currentSessionId === logoutSessionId
            });
            
            // Only process logout if no session ID specified (legacy) or session IDs match
            if (!logoutSessionId || currentSessionId === logoutSessionId) {
              console.log('%c [AUTH SERVICE] Session validation passed, triggering automatic logout', 'background: #FF4500; color: #ffffff; font-size: 12px; padding: 3px;');
              
              // Show appropriate message based on logout reason
              let logoutMessage = 'Your session has ended.';
              if (message['reason'] === 'admin_initiated') {
                logoutMessage = 'Your session was terminated by an administrator.';
              } else if (message['reason'] === 'user_initiated') {
                logoutMessage = 'You have been logged out from another session.';
              } else if (message['reason'] === 'token_expired') {
                logoutMessage = 'Your session has expired.';
              }
              
              // Perform logout without sending another WebSocket event (to prevent loops)
              this.logout(false);
              
              // Show notification (simplified approach)
              try {
                const errorService = this.getErrorService();
                if (errorService) {
                  errorService.showError(logoutMessage);
                } else {
                  console.log(`%c [AUTH SERVICE] ${logoutMessage}`, 'background: #FF0000; color: #ffffff; padding: 5px;');
                }
              } catch (error) {
                console.log(`%c [AUTH SERVICE] ${logoutMessage}`, 'background: #FF0000; color: #ffffff; padding: 5px;');
              }
            } else {
              console.log('%c [AUTH SERVICE] Session validation failed, ignoring logout event from different session', 'background: #FFA500; color: #ffffff; font-size: 12px; padding: 3px;');
            }
          } else {
            console.log('%c [AUTH SERVICE] Logout event is for different user, ignoring', 'background: #FFA500; color: #ffffff; font-size: 12px; padding: 3px;');
          }
          return;
        }
        
        if ('token_validated' === message.type && message.email) {
          // SECURITY FIX: Validate session isolation before processing authentication message
          const messageSessionId = message['session_id'];
          const currentSessionId = this.getCurrentSessionId();
          
          console.log('WebSocket token_validated message details:', {
            email: message.email,
            name: message['name'],
            company: message['company'],
            role: message['role'],
            hasToken: !!message.token,
            exp: message['exp'],
            messageSessionId: messageSessionId,
            currentSessionId: currentSessionId
          });
          
          // Check if this message belongs to the current browser session
          // EXCEPTION: Allow magic link validation even from different sessions
          const isMagicLinkValidation = sessionStorage.getItem('token_being_validated') === 'true';
          
          if (messageSessionId && currentSessionId && messageSessionId !== currentSessionId && !isMagicLinkValidation) {
            console.log(`%c [AUTH SERVICE] 🚫 Ignoring token_validated message from different session`, 'background: #FFA500; color: #ffffff; padding: 5px;', {
              messageSession: messageSessionId,
              currentSession: currentSessionId,
              email: message.email,
              reason: 'Session isolation - preventing cross-browser contamination'
            });
            return; // Ignore messages from other sessions
          }
          
          // Special handling for magic link validation
          if (isMagicLinkValidation) {
            console.log(`%c [AUTH SERVICE] 🔗 Processing magic link token_validated message`, 'background: #32CD32; color: #ffffff; padding: 5px;', {
              email: message.email,
              messageSession: messageSessionId,
              currentSession: currentSessionId,
              reason: 'Magic link validation - session isolation relaxed'
            });
          }
          
          // If no session IDs available, check if this could be meant for us
          if (!messageSessionId && !currentSessionId) {
            console.log(`%c [AUTH SERVICE] ⚠️ Processing legacy token_validated message without session isolation`, 'background: #FFA500; color: #000000; padding: 5px;', {
              email: message.email,
              reason: 'Legacy compatibility - no session IDs available'
            });
          } else if (messageSessionId && currentSessionId && messageSessionId === currentSessionId) {
            console.log(`%c [AUTH SERVICE] ✅ Processing token_validated message for current session`, 'background: #32CD32; color: #000000; padding: 5px;', {
              sessionId: currentSessionId,
              email: message.email
            });
          }
          
          // Extract sub (user ID), company_id, and session_id from token if available
          let sub = message['sub'] || '';
          let company_id = message['company_id'] || '';
          let session_id = message['session_id'] || '';
          
          if (message.token) {
            try {
              const tokenParts = message.token.split('.');
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1]));
                console.log('WebSocket auth token payload:', payload);
                
                // Extract user ID if not already available
                if (!sub && payload.sub) {
                  sub = payload.sub;
                  console.log('Extracted user ID from token:', sub);
                }
                
                // Extract company ID if not already available
                if (!company_id && payload.company_id) {
                  company_id = payload.company_id;
                  console.log('Extracted company ID from token:', company_id);
                }
                
                // Extract session ID if not already available
                if (!session_id && payload.session_id) {
                  session_id = payload.session_id;
                  console.log('Extracted session ID from token:', session_id);
                }
              }
            } catch (e) {
              console.error('Error decoding WebSocket auth token:', e);
            }
          }
          
          let user: User = {
            email: message.email,
            name: message['name'] || '',
            company: message['company'] || '',
            company_id: company_id,
            role: message['role'] || 'user',
            company_type: message['company_type'] || undefined,
            token: message.token || '',
            exp: message['exp'] || null,
            sub: sub,
            session_id: session_id
          };
          
          // Ensure user has valid IDs
          if (message.token) {
            user = this.ensureValidIds(user, message.token);
          }
          
          console.log('Created user object with role:', user.role);
          console.log('User ID (sub):', user.sub);
          
          // Store auth data in localStorage
          console.log(`%c [AUTH SERVICE] 🔗 Storing auth data from WebSocket message`, 'background: #32CD32; color: #ffffff; padding: 5px;');
          
          localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(user));
          localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, message.token || '');
          
          console.log(`%c [AUTH SERVICE] ✅ Auth data stored successfully`, 'color: #32CD32;');
          
          // Clear magic link validation flags if this was a magic link validation
          if (isMagicLinkValidation) {
            sessionStorage.removeItem('token_being_validated');
            sessionStorage.setItem('magic_link_success', 'true');
          }
          
          // Update the current user subject
          this.currentUserSubject.next(user);
          
          // Navigate based on user role after successful authentication
          // MAGIC LINK FIX: Only navigate if not in magic link validation flow
          if (!isMagicLinkValidation) {
            if (user.role === UserRole.PLATFORM_OWNER) {
              console.log('God user detected, navigating to God dashboard');
              this.router.navigate(['/god/overview']);
            } else {
              console.log('Regular user, navigating to dashboard');
              this.router.navigate(['/dashboard']);
            }
          } else {
            console.log(`%c [AUTH SERVICE] 🔗 Magic link: skipping navigation (will be handled by magic link component)`, 'background: #32CD32; color: #ffffff; padding: 5px;');
          }
        } else if ('reconnected' === message.type) {
          console.log('WebSocket reconnected, checking authentication status');
          // Re-check authentication status after reconnection
          if (true === this.isLoggedIn) {
            console.log('User is still logged in after reconnection');
          }
        }
      },
      error: (error) => {
        console.error('WebSocket error in AuthService:', error);
        // Try to reinitialize the connection after a delay, but limit retries
        if (this.wsReconnectAttempts < 3) {
          this.wsReconnectAttempts++;
          console.log(`Attempting to reinitialize WebSocket connection (attempt ${this.wsReconnectAttempts}/3)`);
          setTimeout(() => {
            this.initializeWebSocket();
          }, 5000);
        } else {
          console.log('Max WebSocket reconnection attempts reached, continuing without WebSocket');
          // We'll continue without WebSocket functionality
        }
      },
      complete: () => {
        console.log('WebSocket connection completed');
      }
    });
    
    console.log('WebSocket listeners setup complete');
  }
  
  /**
   * Ensures that the user object has valid MongoDB IDs
   * @param user The user object to validate and update
   * @param token The JWT token to extract IDs from if needed
   * @returns The updated user object with valid IDs
   */
  private ensureValidIds(user: User, token: string): User {
    console.log(`%c [AUTH SERVICE] 🔄 Ensuring valid IDs for user`, 'color: #008080; font-weight: bold;');
    
    // Extract claims from the token
    const claims = this.idValidator.extractJwtClaims(token);
    if (!claims) {
      console.error('Failed to extract claims from token');
      return user;
    }
    
    // Ensure user ID (sub) is set and valid
    if (!this.idValidator.isValidMongoId(user.sub) && claims.sub) {
      user.sub = claims.sub;
      console.log(`%c [AUTH SERVICE] Updated user ID from token: ${user.sub}`, 'color: #008080;');
    }
    
    // Ensure company ID is set and valid
    if (!this.idValidator.isValidMongoId(user.company_id) && claims.company_id) {
      user.company_id = claims.company_id;
      console.log(`%c [AUTH SERVICE] Updated company ID from token: ${user.company_id}`, 'color: #008080;');
    }
    
    return user;
  }
  
  private decodeJwt(token: string): any {
    console.log(`%c [AUTH SERVICE] 🔄 Decoding JWT token`, 'color: #008080; font-weight: bold;');
    return this.idValidator.extractJwtClaims(token);
  }
  
  // Flag to track if a timer is currently being set up to prevent recursive calls

  private setTokenExpiryTimer(expiryTimestamp: number, emitEvent: boolean = true): void {
    console.log('%c [AUTH SERVICE] ⏰ Setting token expiry timer ⏰', 'background: #4682B4; color: #ffffff; font-size: 14px; padding: 5px;');
    
    // Prevent recursive timer setup
    if (true === this.isSettingTimer) {
      console.log('%c [AUTH SERVICE] ⚠️ Preventing recursive timer setup', 'background: #FF0000; color: #ffffff;');
      return;
    }
    
    this.isSettingTimer = true;
    
    if (null !== this.tokenExpiryTimer) {
      console.log('%c [AUTH SERVICE] Clearing existing token expiry timer', 'color: #4682B4; font-weight: bold;');
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    
    const currentTime = Math.floor(Date.now() / 1000);
    const timeToExpiry = (expiryTimestamp - currentTime) * 1000;
    const warningTime = AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD * 1000; // seconds before expiry
    
    console.log('%c [AUTH SERVICE] Token expiry details:', 'color: #4682B4; font-weight: bold;', {
      currentTime,
      expiryTimestamp,
      timeToExpiry: `${timeToExpiry}ms (${timeToExpiry / 1000}s)`,
      warningTime: `${warningTime}ms (${warningTime / 1000}s)`,
      willShowWarning: timeToExpiry > warningTime
    });
    
    // If the token is already within the warning threshold, show the warning immediately
    if (timeToExpiry <= warningTime && timeToExpiry > 0) {
      console.log(`Token will expire soon (in ${timeToExpiry / 1000}s), notifying session monitor immediately`);
      // Token will expire soon, notify immediately but only once
      if (emitEvent && !this.hasNotifiedForExpiry) {
        this.hasNotifiedForExpiry = true;
        this.currentUserSubject.next(this.currentUserValue);
      }
      
      // Reset the timer flag since we're not setting a timer
      this.isSettingTimer = false;
    } 
    // If the token has more time than the warning threshold, set a timer
    else if (timeToExpiry > warningTime) {
      console.log(`Setting token expiry timer to trigger in ${(timeToExpiry - warningTime) / 1000}s`);
      
      // Reset notification flag since we're setting up a new timer
      this.hasNotifiedForExpiry = false;
      
      this.tokenExpiryTimer = setTimeout(() => {
        // Reset the flag when the timer executes
        this.isSettingTimer = false;
        console.log('Token expiry warning timer triggered, notifying session monitor');
        
        // Double-check that the token is still close to expiry
        const currentToken = this.getToken();
        if (currentToken) {
          try {
            const tokenParts = currentToken.split('.');
            if (3 === tokenParts.length) {
              const payload = JSON.parse(atob(tokenParts[1]));
              const currentTime = Math.floor(Date.now() / 1000);
              const remainingTime = payload.exp - currentTime;
              
              console.log(`Token expiry check before warning: ${remainingTime}s remaining`);
              
              // Only emit the event if the token is actually close to expiry and we haven't notified yet
              if (AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD >= remainingTime && emitEvent && !this.hasNotifiedForExpiry) {
                console.log('Token is about to expire, showing warning');
                this.hasNotifiedForExpiry = true;
                this.currentUserSubject.next(this.currentUserValue); // Trigger warning in monitor component
              } else if (remainingTime > AUTH_CONSTANTS.SESSION_EXPIRING_THRESHOLD) {
                console.log('Token was renewed, no need to show warning');
                // Reset the timer with the new expiration time, but only if significantly different
                const newTimeToExpiry = (payload.exp - currentTime) * 1000;
                
                // Only reset if the new expiry time is at least the minimum difference
                if (Math.abs(newTimeToExpiry - timeToExpiry) > AUTH_CONSTANTS.MIN_TOKEN_EXPIRY_DIFF_SECONDS * 1000) {
                  console.log('Significant change in token expiry time, resetting timer');
                  this.setTokenExpiryTimer(payload.exp, emitEvent);
                } else {
                  console.log('Token expiry time similar to current timer, not resetting');
                  // Reset the flag to allow future timer setups
                  this.isSettingTimer = false;
                }
              } else {
                // Reset the flag to allow future timer setups
                this.isSettingTimer = false;
              }
            }
          } catch (error) {
            console.error('Error checking token expiry before warning:', error);
            this.isSettingTimer = false;
          }
        } else {
          this.isSettingTimer = false;
        }
      }, timeToExpiry - warningTime);
    } else {
      console.log('Token is already expired, logging out');
      this.logout(true); // Send WebSocket event for token expiry
    }
  }
  
  public logout(sendWebSocketEvent: boolean = true): void {
    console.log('%c [AUTH SERVICE] 🚪 Logging out user 🚪', 'background: #FF4500; color: #ffffff; font-size: 14px; padding: 5px;');
    
    // Call server logout endpoint to broadcast to other sessions
    if (sendWebSocketEvent && this.currentUserValue) {
      console.log('%c [AUTH SERVICE] Calling server logout endpoint to broadcast event', 'background: #FF6B00; color: #ffffff; font-size: 12px; padding: 3px;');
      try {
        // Call server logout endpoint which will broadcast to WebSocket clients
        this.http.post(`${this.apiUrl}/auth/logout`, {}, {
          headers: {
            'Authorization': `Bearer ${this.getToken()}`
          }
        }).subscribe({
          next: () => {
            console.log('%c [AUTH SERVICE] Server logout endpoint called successfully', 'background: #28a745; color: #ffffff; font-size: 12px; padding: 3px;');
          },
          error: (error) => {
            console.error('Failed to call server logout endpoint:', error);
          }
        });
      } catch (error) {
        console.error('Error calling server logout endpoint:', error);
      }
    }
    
    // Close WebSocket connection if it exists
    if (null !== this.wsService) {
      console.log('Closing WebSocket connection on logout');
      this.wsService.closeConnection();
    }
    
    // Unsubscribe from WebSocket subscription
    if (this.wsSubscription) {
      this.wsSubscription.unsubscribe();
      this.wsSubscription = null;
    }
    
    // Clear auth data from localStorage
    localStorage.removeItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
    localStorage.removeItem('magic_link_pending');
    console.log('[AUTH SERVICE] Auth data cleared from localStorage');

    // Clean up cross-tab listeners
    this.cleanupCrossTabListener();

    this.currentUserSubject.next(null);
    
    // Clear token expiry timer
    if (null !== this.tokenExpiryTimer) {
      console.log('Clearing token expiry timer on logout');
      clearTimeout(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
    
    // Reset flags to prevent issues with timer management
    this.isSettingTimer = false;
    this.isCheckingExpiry = false;
    
    // Navigate to dashboard (login will be handled by session monitor)
    this.router.navigate(['/dashboard']);
  }
  
  /**
   * Initiates a remote logout for a specific user (admin function)
   * @param userId - The user ID to logout
   * @param reason - The reason for the logout
   */
  public remoteLogout(userId: string, reason: string = 'admin_initiated'): void {
    console.log('%c [AUTH SERVICE] 🔒 Initiating remote logout 🔒', 'background: #8B0000; color: #ffffff; font-size: 14px; padding: 5px;');
    
    if (this.wsService) {
      try {
        this.wsService.send({
          type: 'logout',
          user_id: userId,
          company_id: this.currentUserValue?.company_id,
          timestamp: Date.now(),
          reason: reason
        });
        console.log(`Remote logout initiated for user: ${userId}`);
      } catch (error) {
        console.error('Failed to send remote logout via WebSocket:', error);
      }
    } else {
      console.error('WebSocket not available for remote logout');
    }
  }
  
  // Flag to prevent multiple simultaneous token expiry checks
  private isCheckingExpiry: boolean = false;
  
  public checkTokenExpiry(): void {
    // Prevent multiple simultaneous checks
    if (true === this.isCheckingExpiry) {
      console.log('Already checking token expiry, skipping duplicate check');
      return;
    }
    
    this.isCheckingExpiry = true;
    
    try {
      const token = this.getToken(true); // Silent mode to reduce logging
      console.log('Checking token expiry, token exists:', null !== token);
      
      if (token) {
        const decodedToken = this.decodeJwt(token);
        console.log('Token decoded:', null !== decodedToken);
        
        if (decodedToken) {
          const currentTime = Math.floor(Date.now() / 1000);
          console.log('Token expiry check:', {
            currentTime,
            tokenExpiry: decodedToken.exp,
            timeRemaining: `${decodedToken.exp - currentTime}s`,
            isExpired: decodedToken.exp < currentTime
          });
          
          if (decodedToken.exp < currentTime) {
            // Token has expired
            console.log('Token has expired, logging out');
            this.logout(true); // Send WebSocket event for token expiry
          } else {
            // Token still valid, set expiry timer
            console.log('Token still valid, setting expiry timer');
            this.setTokenExpiryTimer(decodedToken.exp);
          }
        }
      }
    } finally {
      // Always reset the flag when done
      this.isCheckingExpiry = false;
    }
  }
  
  // This flag helps components know if a token was recently validated
  private _tokenValidationStatus = new BehaviorSubject<boolean | null>(null);
  public tokenValidationStatus$ = this._tokenValidationStatus.asObservable();
  
  public validateTokenFromUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      console.log('Found token in URL, processing it');
      // Set sessionStorage flag for magic link component
      sessionStorage.setItem('token_being_validated', 'true');
      
      // Clear token from URL without page refresh
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Validate token with backend
      this.validateToken(token).subscribe({
        next: (response) => {
          console.log('Token validation response:', response);
          // Set the validation status for components to listen to
          this._tokenValidationStatus.next(response.success);
          
          // Store the success status in sessionStorage for page refreshes
          if (response.success) {
            sessionStorage.setItem('magic_link_success', 'true');
          }
          
          // Token validation is handled by WebSocket
          console.log('Token validation initiated');
        },
        error: (err) => {
          console.error('Token validation error:', err);
          this._tokenValidationStatus.next(false);
          sessionStorage.removeItem('token_being_validated');
        }
      });
    } else {
      console.log('No token found in URL');
    }
  }
  
  public validateToken(token: string): Observable<{ success: boolean, message: string, token?: string }> {
    return this.http.post<{ success: boolean, message: string, token?: string }>(
      `${config.apiUrl}/auth/validate-token`,
      { token }
    ).pipe(
      tap(response => {
        if (response.success && response.token) {
          console.log('[AUTH SERVICE] Processing JWT token from magic link validation');

          const decodedToken = this.decodeJwt(response.token);
          if (decodedToken) {
            // MAGIC LINK SESSION CONTINUITY FIX: Store with comprehensive key patterns
            const isMagicLinkValidation = sessionStorage.getItem('token_being_validated') === 'true';

            // Store the validated token
            localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, response.token);
            localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(decodedToken));
            console.log('[AUTH SERVICE] Token validation stored successfully');

            this.currentUserSubject.next(decodedToken);

            // Broadcast to other tabs (original login tab) so they can authenticate
            if (isMagicLinkValidation) {
              this.broadcastMagicLinkSuccess(decodedToken, response.token);
            }
          }
        }
      })
    );
  }
  
  private handleResponse<T>(response: HttpResponse<T>): T {
    console.log('%c [AUTH SERVICE] 📥 Handling HTTP response', 'color: #1E90FF;');
    console.log('%c [AUTH SERVICE] Response status:', 'color: #1E90FF;', response.status);
    console.log('%c [AUTH SERVICE] Response headers:', 'color: #1E90FF;', response.headers.keys());
    
    // Check if there's a renewed token in the headers
    const renewedToken = response.headers.get('X-Renewed-Token');
    if (null !== renewedToken) {
      console.log('%c [AUTH SERVICE] 🔑 Received renewed token from server', 'background: #1E90FF; color: #ffffff; font-weight: bold;');
      console.log(`%c [AUTH SERVICE] Renewed token length: ${renewedToken.length}`, 'color: #1E90FF;');
      console.log(`%c [AUTH SERVICE] Renewed token first 10 chars: ${renewedToken.substring(0, 10)}...`, 'color: #1E90FF;');
      
      // Update the stored token
      this.setToken(renewedToken);
    } else {
      console.log('%c [AUTH SERVICE] No renewed token in response', 'color: #1E90FF;');
    }
    return response.body as T;
  }
  
  // The intercept method has been removed to avoid duplicate interceptor functionality.
  // Authentication is now handled by the standalone authInterceptor in auth.interceptor.ts
  
  private setToken(token: string): void {
    console.log('%c [AUTH SERVICE] 🔐 Setting token in localStorage', 'color: #008080; font-weight: bold;');
    console.log(`%c [AUTH SERVICE] Token length: ${token.length}`, 'color: #008080;');
    console.log(`%c [AUTH SERVICE] Token first 10 chars: ${token.substring(0, 10)}...`, 'color: #008080;');
    localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, token);
    console.log('%c [AUTH SERVICE] ✅ Token saved to localStorage', 'color: #008080; font-weight: bold;');
  }
  
  /**
   * Updates the JWT token and refreshes the user data
   * @param token New JWT token
   */
  public updateToken(token: string): void {
    console.log('%c [AUTH SERVICE] 🔄 UPDATING TOKEN 🔄', 'background: #006400; color: #ffffff; font-size: 16px; padding: 10px;');
    console.log(`%c [AUTH SERVICE] New token length: ${token.length}`, 'color: #006400;');
    console.log(`%c [AUTH SERVICE] New token first 10 chars: ${token.substring(0, 10)}...`, 'color: #006400;');
    
    try {
      // Check if the token is different from the current one
      const currentToken = localStorage.getItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY);
      if (currentToken === token) {
        console.log(`%c [AUTH SERVICE] ⚠️ New token is identical to current token`, 'background: #FFA500; color: #000000; font-weight: bold;');
      } else if (currentToken) {
        console.log(`%c [AUTH SERVICE] Current token length: ${currentToken.length}`, 'color: #006400;');
        console.log(`%c [AUTH SERVICE] Current token first 10 chars: ${currentToken.substring(0, 10)}...`, 'color: #006400;');
      }
      
      // Decode the token to get user info
      const tokenParts = token.split('.');
      console.log(`%c [AUTH SERVICE] Token parts count: ${tokenParts.length}`, 'color: #006400;');
      
      if (3 === tokenParts.length) {
        console.log(`%c [AUTH SERVICE] Token has valid structure (3 parts)`, 'color: #006400;');
        
        try {
          const payload = JSON.parse(atob(tokenParts[1]));
          console.log(`%c [AUTH SERVICE] Token payload decoded:`, 'color: #006400;', payload);
          
          // Check if the token is already expired
          const currentTime = Math.floor(Date.now() / 1000);
          const timeToExpiry = payload.exp - currentTime;
          
          console.log(`%c [AUTH SERVICE] Token expiration check:`, 'color: #006400;');
          console.log(`%c [AUTH SERVICE] Current time: ${currentTime}`, 'color: #006400;');
          console.log(`%c [AUTH SERVICE] Token expiry: ${payload.exp}`, 'color: #006400;');
          console.log(`%c [AUTH SERVICE] Time to expiry: ${timeToExpiry}s`, 
            timeToExpiry > 300 ? 'color: #006400;' : 'color: #FF0000; font-weight: bold;');
          
          if (payload.exp <= currentTime) {
            console.log(`%c [AUTH SERVICE] ⚠️ Token is already expired, not updating`, 'background: #FF0000; color: #ffffff; font-weight: bold;');
            return;
          }
          
          // Get the current user from localStorage
          const currentUserStr = localStorage.getItem(AUTH_CONSTANTS.USER_STORAGE_KEY);
          console.log(`%c [AUTH SERVICE] Current user in localStorage: ${currentUserStr ? 'Found' : 'Not found'}`, 'color: #006400;');
          
          if (currentUserStr) {
            try {
              const user = JSON.parse(currentUserStr);
              console.log(`%c [AUTH SERVICE] Current user parsed:`, 'color: #006400;', {
                email: user.email,
                name: user.name,
                role: user.role,
                company: user.company,
                hasToken: !!user.token
              });
              
              // Update the token
              console.log(`%c [AUTH SERVICE] Saving token to localStorage`, 'color: #006400; font-weight: bold;');
              localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, token);
              
              // Update the user object with the new token
              console.log(`%c [AUTH SERVICE] Updating user object with new token`, 'color: #006400;');
              user.token = token;
              
              // Save the updated user to localStorage
              console.log(`%c [AUTH SERVICE] Saving updated user to localStorage`, 'color: #006400;');
              localStorage.setItem(AUTH_CONSTANTS.USER_STORAGE_KEY, JSON.stringify(user));
              
              // Notify subscribers, but don't trigger a full user state change
              // that might cause navigation disruptions
              console.log(`%c [AUTH SERVICE] Notifying subscribers of token update only`, 'color: #006400;');
              
              // Create a shallow copy of the current user value to avoid triggering
              // unnecessary navigation or component reloads
              const currentUser = this.currentUserValue;
              if (currentUser) {
                // Update token
                currentUser.token = token;
                
                // Ensure all IDs are valid
                const updatedCurrentUser = this.ensureValidIds(currentUser, token);
                
                // Use object reference equality to minimize component re-renders
                this.currentUserSubject.next({...updatedCurrentUser});
              } else {
                // If no current user, use the full user object with validated IDs
                const updatedUser = this.ensureValidIds(user, token);
                this.currentUserSubject.next(updatedUser);
              }
              
              // Set up token expiry timer
              console.log(`%c [AUTH SERVICE] Setting up token expiry timer`, 'color: #006400;');
              this.setTokenExpiryTimer(payload.exp);
              
              const expiryDate = new Date(payload.exp * 1000).toISOString();
              console.log(`%c [AUTH SERVICE] ✅ Token updated successfully, new expiry: ${expiryDate}`, 'background: #006400; color: #ffffff; font-weight: bold;');
            } catch (parseError) {
              console.log(`%c [AUTH SERVICE] ⚠️ Error parsing current user: ${parseError}`, 'background: #FF0000; color: #ffffff;');
            }
          } else {
            console.log(`%c [AUTH SERVICE] ⚠️ No user found in localStorage, saving token only`, 'background: #FFA500; color: #000000;');
            localStorage.setItem(AUTH_CONSTANTS.TOKEN_STORAGE_KEY, token);
          }
        } catch (decodeError) {
          console.log(`%c [AUTH SERVICE] ⚠️ Error decoding token payload: ${decodeError}`, 'background: #FF0000; color: #ffffff;');
        }
      } else {
        console.log(`%c [AUTH SERVICE] ⚠️ Invalid token format: expected 3 parts, got ${tokenParts.length}`, 'background: #FF0000; color: #ffffff;');
      }
    } catch (error) {
      console.log(`%c [AUTH SERVICE] ⚠️ Error updating token: ${error}`, 'background: #FF0000; color: #ffffff;');
    }
  }

  /**
   * Refreshes the user's token
   * @returns An observable with the refresh response
   */
  public refreshToken(): Observable<any> {
    console.log('Attempting to refresh token');
    const token = this.getToken();
    
    if (!token) {
      console.error('No token available to refresh');
      return throwError(() => new Error('No token available to refresh'));
    }
    
    return this.http.post<any>(`${this.apiUrl}/auth/refresh-token`, {}, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }).pipe(
      tap(response => {
        if (response && response.token) {
          console.log('Token refreshed successfully');
          this.updateToken(response.token);
        }
      }),
      catchError(error => {
        console.error('Token refresh failed:', error);
        if (401 === error.status) {
          // If the server rejects the refresh (token expired), logout
          this.logout(true); // Send WebSocket event for token expiry
        }
        return throwError(() => error);
      })
    );
  }

  /**
   * Get current session ID from JWT token
   * @returns session_id or null if not available
   */
  private getCurrentSessionId(): string | null {
    const currentUser = this.currentUserValue;
    if (currentUser?.token) {
      try {
        const tokenParts = currentUser.token.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          return payload.session_id || null;
        }
      } catch (e) {
        console.error('[AUTH SERVICE] Error extracting session ID from token:', e);
      }
    }
    return null;
  }

}
